import type { ReactNode } from 'react';
import { useEffect } from 'react';

// DetailFlyout — fixed right-panel overlay for row details.
// Bundle reference: ui-kits/officer-console.html lines 140-175.
//
// Behavior:
// - Open is controlled (parent owns state).
// - Esc key closes when open + onClose provided.
// - Backdrop click does NOT auto-close (callers wire it explicitly if desired).
// - Width clamps to 420px on desktop, full-width on mobile.

export type DetailFlyoutProps = {
  open: boolean;
  onClose?: () => void;
  title: ReactNode;
  subtitle?: ReactNode;
  actions?: ReactNode;
  children: ReactNode;
};

export function DetailFlyout({ open, onClose, title, subtitle, actions, children }: DetailFlyoutProps) {
  useEffect(() => {
    if (!open || !onClose) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose?.();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <aside
      role="dialog"
      aria-modal="false"
      className="fixed inset-y-0 right-0 z-40 w-full sm:w-[420px] bg-card border-l border-border shadow-popover flex flex-col"
    >
      <header className="px-5 py-4 border-b border-border flex items-start justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="text-base font-bold text-foreground truncate">{title}</div>
          {subtitle ? (
            <div className="text-xs text-foreground-mute truncate">{subtitle}</div>
          ) : null}
        </div>
        {onClose ? (
          <button
            type="button"
            onClick={onClose}
            aria-label="Close detail"
            className="text-foreground-mute hover:text-foreground p-1"
          >
            <span aria-hidden>×</span>
          </button>
        ) : null}
      </header>

      <div className="flex-1 overflow-y-auto px-5 py-4 flex flex-col gap-5">{children}</div>

      {actions ? (
        <footer className="px-5 py-3 border-t border-border flex items-center justify-end gap-2 bg-muted">
          {actions}
        </footer>
      ) : null}
    </aside>
  );
}

// KVGrid — key/value list for the flyout body. 2-column grid: label left, value right.

export type KVGridProps = {
  rows: ReadonlyArray<{ label: string; value: ReactNode }>;
};

export function KVGrid({ rows }: KVGridProps) {
  return (
    <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-2">
      {rows.map((row, i) => (
        <div key={i} className="contents">
          <dt className="text-xs text-foreground-mute">{row.label}</dt>
          <dd className="text-sm text-foreground text-right break-words">{row.value}</dd>
        </div>
      ))}
    </dl>
  );
}

// CheckList — compliance checks list with ✓/⚠/✕ markers + dashed dividers.

export type CheckListItem = {
  status: 'ok' | 'warn' | 'fail';
  label: string;
  detail?: string;
};

export type CheckListProps = {
  items: ReadonlyArray<CheckListItem>;
};

const CHECK_GLYPH: Record<CheckListItem['status'], string> = {
  ok: '✓',
  warn: '⚠',
  fail: '✕',
};

const CHECK_TONE: Record<CheckListItem['status'], string> = {
  ok: 'text-success',
  warn: 'text-warn',
  fail: 'text-danger',
};

export function CheckList({ items }: CheckListProps) {
  return (
    <ul className="flex flex-col">
      {items.map((it, i) => (
        <li
          key={i}
          className="flex items-start gap-3 py-2 border-b border-dashed border-border-soft last:border-b-0"
        >
          <span aria-hidden className={`mt-0.5 text-sm ${CHECK_TONE[it.status]}`}>{CHECK_GLYPH[it.status]}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm text-foreground">{it.label}</div>
            {it.detail ? <div className="text-xs text-foreground-mute mt-0.5">{it.detail}</div> : null}
          </div>
        </li>
      ))}
    </ul>
  );
}
